import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { useNotifier } from '../../../context/NotificationContext';

const BrandIdentityTab = ({ config, onUpdate }) => {
  const notify = useNotifier();
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(config?.logoUrl || '');

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setLogoFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          setLogoPreview(e.target.result);
          onUpdate({ logoUrl: e.target.result });
        };
        reader.readAsDataURL(file);
      } else {
        notify.warning('Please select a valid image file');
      }
    }
  };

  const handleInputChange = (field, value) => {
    onUpdate({ [field]: value });
  };

  const handleColorChange = (colorType, value) => {
    onUpdate({
      brandColors: {
        ...config?.brandColors,
        [colorType]: value
      }
    });
  };

  return (
    <div className="space-y-8">
      {/* Company Information */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Building" size={20} className="mr-2 text-[#2D9B9B]" />
          Company Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Company Name
            </label>
            <input
              type="text"
              value={config?.companyName || ''}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              className="w-full px-4 py-3 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-[#2D9B9B] focus:border-[#2D9B9B] transition-colors"
              placeholder="Enter company name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Tagline
            </label>
            <input
              type="text"
              value={config?.tagline || ''}
              onChange={(e) => handleInputChange('tagline', e.target.value)}
              className="w-full px-4 py-3 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-[#2D9B9B] focus:border-[#2D9B9B] transition-colors"
              placeholder="Enter company tagline"
            />
          </div>
        </div>
      </div>

      {/* Logo Upload */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Image" size={20} className="mr-2 text-[#2D9B9B]" />
          Logo Configuration
        </h3>
        <div className="bg-secondary-50 border-2 border-dashed border-secondary-300 rounded-xl p-8">
          <div className="text-center">
            {logoPreview ? (
              <div className="mb-4">
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="max-h-32 mx-auto rounded-lg shadow-md"
                />
              </div>
            ) : (
              <div className="mb-4">
                <div className="w-24 h-24 mx-auto bg-gradient-to-br from-[#2D9B9B] to-[#1A7A7A] rounded-lg flex items-center justify-center">
                  <Icon name="HardHat" size={32} color="white" />
                </div>
              </div>
            )}
            <div className="space-y-3">
              <p className="text-sm text-text-secondary">
                Upload your company logo (PNG, JPG, SVG recommended)
              </p>
              <label className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#2D9B9B] to-[#1A7A7A] text-white rounded-lg hover:from-[#1A7A7A] hover:to-[#2D9B9B] cursor-pointer transition-all duration-200">
                <Icon name="Upload" size={16} />
                <span>Choose Logo File</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </label>
              <p className="text-xs text-text-muted">
                Recommended: 200x200px minimum, transparent background
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Brand Colors */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Palette" size={20} className="mr-2 text-[#2D9B9B]" />
          Brand Colors
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Primary Color
            </label>
            <div className="flex items-center space-x-3">
              <div 
                className="w-12 h-12 rounded-lg border-2 border-secondary-200 shadow-sm"
                style={{ backgroundColor: config?.brandColors?.primary || '#2D9B9B' }}
              />
              <input
                type="color"
                value={config?.brandColors?.primary || '#2D9B9B'}
                onChange={(e) => handleColorChange('primary', e.target.value)}
                className="w-16 h-12 rounded-lg border border-secondary-200 cursor-pointer"
              />
              <input
                type="text"
                value={config?.brandColors?.primary || '#2D9B9B'}
                onChange={(e) => handleColorChange('primary', e.target.value)}
                className="flex-1 px-3 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-[#2D9B9B] focus:border-[#2D9B9B] text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Secondary Color
            </label>
            <div className="flex items-center space-x-3">
              <div 
                className="w-12 h-12 rounded-lg border-2 border-secondary-200 shadow-sm"
                style={{ backgroundColor: config?.brandColors?.secondary || '#1A7A7A' }}
              />
              <input
                type="color"
                value={config?.brandColors?.secondary || '#1A7A7A'}
                onChange={(e) => handleColorChange('secondary', e.target.value)}
                className="w-16 h-12 rounded-lg border border-secondary-200 cursor-pointer"
              />
              <input
                type="text"
                value={config?.brandColors?.secondary || '#1A7A7A'}
                onChange={(e) => handleColorChange('secondary', e.target.value)}
                className="flex-1 px-3 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-[#2D9B9B] focus:border-[#2D9B9B] text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Accent Color
            </label>
            <div className="flex items-center space-x-3">
              <div 
                className="w-12 h-12 rounded-lg border-2 border-secondary-200 shadow-sm"
                style={{ backgroundColor: config?.brandColors?.accent || '#3DB8B8' }}
              />
              <input
                type="color"
                value={config?.brandColors?.accent || '#3DB8B8'}
                onChange={(e) => handleColorChange('accent', e.target.value)}
                className="w-16 h-12 rounded-lg border border-secondary-200 cursor-pointer"
              />
              <input
                type="text"
                value={config?.brandColors?.accent || '#3DB8B8'}
                onChange={(e) => handleColorChange('accent', e.target.value)}
                className="flex-1 px-3 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-[#2D9B9B] focus:border-[#2D9B9B] text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Brand Guidelines */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="BookOpen" size={20} className="mr-2 text-[#2D9B9B]" />
          Brand Guidelines
        </h3>
        <div className="bg-gradient-to-r from-[#2D9B9B]/5 to-[#1A7A7A]/5 border border-[#2D9B9B]/20 rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-text-primary mb-3">Logo Usage</h4>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Maintain minimum clear space around logo</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Use high-contrast backgrounds</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Preserve aspect ratio when scaling</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-text-primary mb-3">Color Guidelines</h4>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Primary color for main CTAs and headers</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Secondary color for supporting elements</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Accent color for highlights and emphasis</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandIdentityTab;